import React from "react";
import {
    additivesText, CHANNEL_LABELS, channelsForNeedle, polymersText, setupChannels, solventsText, stockLabel,
} from "../solutionText";

const measuredText = (sol = {}) => [
    sol.viscosity && `${sol.viscosity} ${sol.viscosityUnit || 'cP'}`,
    sol.surfaceTension && `${sol.surfaceTension} ${sol.surfaceTensionUnit || 'mN/m'}`,
    sol.conductivity && `${sol.conductivity} ${sol.conductivityUnit || 'mS/cm'}`,
    sol.evaporationRate && `${sol.evaporationRate} ${sol.evaporationRateUnit || 'mg/cm²/hr'}`,
    (sol.PH || sol.PH === 0) && `pH ${sol.PH}`,
].filter(Boolean).join(' · ');

/** "PCL 10 wt% in DCM:DMF 7:3 · with AgNO3 2 wt%" */
const stockText = (s) => {
    const polymers = polymersText(s.polymers);
    const solvents = solventsText(s.solvents);
    const concentration = s.concentration ? ` ${s.concentration} ${s.concentrationUnit || 'wt%'}` : '';
    const main = polymers ? `${polymers}${concentration}${solvents ? ` in ${solvents}` : ''}` : (solvents || '—');
    const additives = additivesText(s.additives);
    return additives ? `${main} · with ${additives}` : main;
};

/** One feed: a single solution, or "Mixed 70:30 v/v" with its solutions A, B, ... */
const FeedLines = ({ feed }) => {
    const stocks = feed.stocks || [];
    if (stocks.length <= 1) return <span>{stocks[0] ? stockText(stocks[0]) : '—'}</span>;
    return (
        <>
            <span className="badge bg-primary-subtle text-primary-emphasis border">
                Mixed {stocks.map(s => s.share ?? '?').join(':')} {feed.mixRatioUnit}
            </span>
            {stocks.map((s, i) => (
                <div key={i} className="ms-2"><strong>{stockLabel(s, i)}:</strong> {stockText(s)}</div>
            ))}
        </>
    );
};

/** The setup card's overview of the polymer solution(s): what was dissolved in what, mixed how, per channel. */
export default function SolutionPreparationSummary({ setup }) {
    const channels = setupChannels(setup);
    const needleChannels = channelsForNeedle(setup.needleProperty?.needleType);
    const sol = setup.solutionProperty || {};

    if (channels.length) {
        const mismatch = channels.join() !== needleChannels.join();
        return (
            <div className="small">
                <span className="badge bg-primary-subtle text-primary-emphasis border mb-2">
                    {channels.map(c => CHANNEL_LABELS[c]).join(' / ')} solutions
                </span>
                {mismatch && (
                    <div className="text-danger mb-1">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Does not match the {setup.needleProperty?.needleType || 'chosen'} needle: click to update.
                    </div>
                )}
                {setup.solutions.filter(f => f.channel !== 'MAIN').map(f => (
                    <div key={f.channel} className="mb-1">
                        <strong>{CHANNEL_LABELS[f.channel]}:</strong> <FeedLines feed={f} />
                        {measuredText(f.properties) && <div className="text-muted ms-2">{measuredText(f.properties)}</div>}
                    </div>
                ))}
            </div>
        );
    }

    const feed = setup.solutions?.[0];
    if (feed?.stocks?.length > 1) {
        return (
            <div className="small">
                <FeedLines feed={feed} />
                {measuredText(sol) && <div className="text-muted">{measuredText(sol)}</div>}
            </div>
        );
    }

    const polymers = polymersText(setup.polymerProperty?.polymerComponents);
    const solvents = solventsText(setup.solventProperty?.solventComponents);
    if (!polymers && !solvents && !sol.concentration) {
        return (
            <span style={{ color: "#888" }}>
                Click to describe the polymer solution
                {needleChannels.length > 0 && <> (one per channel for the {setup.needleProperty.needleType.toLowerCase()} needle)</>}
            </span>
        );
    }
    const named = (setup.polymerProperty?.polymerComponents || []).filter(p => p?.polymerName);
    const kind = named.length > 1 ? 'Polymer blend'
        : named.length === 1 && (named[0].copolymer || named[0].newPolymer?.polymerType === 'COPOLYMER') ? 'Copolymer' : null;
    const pending = named.filter(p => p.newPolymer).map(p => p.polymerName);
    return (
        <div className="small">
            {kind && <span className="badge bg-primary-subtle text-primary-emphasis border mb-2">{kind}</span>}
            <div>
                <strong>{polymers || 'No polymer yet'}</strong>
                {sol.concentration ? ` ${sol.concentration} ${sol.concentrationUnit || 'wt%'}` : ''}
                {solvents ? ` in ${solvents}` : ''}
            </div>
            {additivesText(setup.additives) && <div className="text-muted">with {additivesText(setup.additives)}</div>}
            {pending.length > 0 && (
                <span className="badge bg-warning text-dark mt-1" style={{ whiteSpace: 'normal' }}>
                    new: {pending.join(', ')} · awaits approval
                </span>
            )}
            {measuredText(sol) && <div className="text-muted">{measuredText(sol)}</div>}
            {needleChannels.length > 0 && (
                <div className="text-muted mt-1">
                    <i className="bi bi-info-circle me-1"></i>One solution for the whole {setup.needleProperty.needleType.toLowerCase()} needle; click to describe each channel.
                </div>
            )}
        </div>
    );
}
